import { zodResolver } from '@hookform/resolvers/zod';
import { updateProductSchema, type Product, type UpdateProductInput } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';

export interface ProductDialogProps {
  open: boolean;
  product: Product;
  error?: string | null;
  busy?: boolean;
  onSubmit: (values: UpdateProductInput) => void;
  onClose: () => void;
}

/** Modifica di nome e prefisso di un prodotto; il prefisso è bloccato dal primo ticket (DEC-003). */
export function ProductDialog({ open, product, error = null, busy = false, onSubmit, onClose }: ProductDialogProps) {
  const { control, handleSubmit } = useForm<UpdateProductInput>({
    resolver: zodResolver(updateProductSchema),
    defaultValues: { name: product.name, prefix: product.prefix },
  });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="product-dialog-title"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle id="product-dialog-title">Modifica {product.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label="Nome del prodotto"
                  required
                  autoFocus
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="prefix"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label="Prefisso"
                  required
                  disabled={product.prefixLocked}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    (product.prefixLocked
                      ? 'È già nel codice di ticket esistenti: non si cambia più.'
                      : '2-5 lettere, unico fra i prodotti. Fisso dal primo ticket.')
                  }
                  slotProps={{ htmlInput: { maxLength: 5, style: { textTransform: 'uppercase' } } }}
                />
              )}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Salva
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
